type State = {
  pageRendered: boolean
  listenerAdded: boolean
}

type UsePageDependencies = {
  pageRendered: ComputedRef<boolean>
}

/**
 * This composable is a workaround for loading the correct menu and footer
 * on the client side based on the active channel.
 *
 * The nuxt-page-dependencies module only handles the rendering on the server
 * side. However, in our case the footer is also dependent on the active
 * channel on the client side.
 *
 * For logged in users or on pages where SSR is disabled, this would result
 * in loading the wrong footer initially, because the footer component is
 * already being rendered before the page has finished rendering.
 *
 * This composable returns a computed property that indicates whether the
 * page has rendered or not.
 */
export function usePageDependencies(): UsePageDependencies {
  const app = useNuxtApp()
  const state = useState<State>('pageDependencies', () => {
    // This property is set by the module once the page has rendered.
    // We can also assume that the page has already been rendered when we are
    // on the server.
    const pageRendered =
      !!app._nuxtPageDependenciesRendered || import.meta.server

    return {
      pageRendered,
      listenerAdded: false,
    }
  })

  // On the server, the module already handles everything correctly.
  // Since there is also no reactivity on SSR, we can just return true.
  if (import.meta.server) {
    return {
      pageRendered: computed(() => true),
    }
  }

  // Only add the event listener when needed.
  if (!state.value.pageRendered && !state.value.listenerAdded) {
    state.value.listenerAdded = true
    // This event is emitted when calling the renderPageDependencies()
    // composable in page components.
    app.hook('nuxt-page-dependencies:rendered', function () {
      state.value.pageRendered = true
    })
  }

  return {
    pageRendered: computed(() => state.value.pageRendered),
  }
}
